import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../app/auth-context';
import { ApiError, api } from '../../lib/api';

/**
 * Seguranca da conta: 2FA, codigos de recuperacao, senha e dispositivos.
 *
 * Os codigos de recuperacao aparecem UMA vez. Depois disso nao existe forma de
 * recupera-los — so gerar novos, o que invalida os anteriores.
 */
export function SecurityPage() {
  const { user, reload } = useAuth();
  const [params] = useSearchParams();
  const firstAccess = params.get('primeiro-acesso') === '1';

  return (
    <div className="main">
      <header className="page-header">
        <h1>Seguranca</h1>
        <p>
          Esta tela concentra tudo que protege o acesso as suas financas: segunda etapa, senha e
          dispositivos conectados.
        </p>
      </header>

      {firstAccess && !user?.totpEnabled && (
        <div className="alert warning" role="status">
          Conta criada. Ative a verificacao em duas etapas antes de cadastrar qualquer dado
          financeiro.
        </div>
      )}

      <div className="stack">
        <TotpCard enabled={Boolean(user?.totpEnabled)} onChange={reload} />
        <PasswordCard />
        <SessionsCard />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------- 2FA

function TotpCard({ enabled, onChange }: { enabled: boolean; onChange: () => Promise<void> }) {
  const [setup, setSetup] = useState<{ secret: string; qrCodeDataUrl: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const startSetup = useMutation({
    mutationFn: api.totpSetup,
    onSuccess: (data) => {
      setSetup(data);
      setError(null);
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const confirm = useMutation({
    mutationFn: () => api.totpConfirm(code.replace(/\D/g, '')),
    onSuccess: async (data) => {
      setRecoveryCodes(data.recoveryCodes);
      setSetup(null);
      setCode('');
      setError(null);
      await onChange();
    },
    onError: (caught) => setError(messageFor(caught)),
  });

  const regenerate = useMutation({
    mutationFn: api.regenerateRecoveryCodes,
    onSuccess: (data) => setRecoveryCodes(data.recoveryCodes),
    onError: (caught) => setError(messageFor(caught)),
  });

  if (recoveryCodes) {
    return (
      <section className="card">
        <h2 className="card-title">Guarde os codigos de recuperacao</h2>
        <p className="card-hint">
          Sao a unica forma de entrar se voce perder o celular. Cada um serve uma vez. Esta tela nao
          volta a exibi-los.
        </p>

        <div className="codes">
          {recoveryCodes.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>

        <div className="row">
          <button
            className="btn secondary"
            onClick={() => navigator.clipboard.writeText(recoveryCodes.join('\n'))}
          >
            Copiar todos
          </button>
          <button className="btn" onClick={() => setRecoveryCodes(null)}>
            Ja guardei
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <div className="between">
        <div>
          <h2 className="card-title">Verificacao em duas etapas</h2>
          <p className="card-hint" style={{ marginBottom: 0 }}>
            Um codigo temporario do celular, alem da senha.
          </p>
        </div>
        <span className={enabled ? 'badge accent' : 'badge'}>{enabled ? 'Ativa' : 'Inativa'}</span>
      </div>

      {error && (
        <div className="alert error" role="alert" style={{ marginTop: 'var(--space-4)' }}>
          {error}
        </div>
      )}

      {enabled ? (
        <div className="row" style={{ marginTop: 'var(--space-4)' }}>
          <button
            className="btn secondary"
            onClick={() => regenerate.mutate()}
            disabled={regenerate.isPending}
          >
            Gerar novos codigos de recuperacao
          </button>
        </div>
      ) : setup ? (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <p className="card-hint">
            Leia o QR Code no Google Authenticator, Authy, 1Password ou Bitwarden.
          </p>
          <img
            className="qr"
            src={setup.qrCodeDataUrl}
            alt="QR Code da verificacao em duas etapas"
            width={200}
            height={200}
          />
          <p className="center muted" style={{ fontSize: 'var(--text-xs)' }}>
            Nao consegue ler? Digite: <span className="mono">{setup.secret}</span>
          </p>

          <div className="field" style={{ marginTop: 'var(--space-5)' }}>
            <label htmlFor="totp">Codigo gerado pelo aplicativo</label>
            <input
              id="totp"
              className="code-input"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>

          <button
            className="btn block"
            onClick={() => confirm.mutate()}
            disabled={code.replace(/\D/g, '').length !== 6 || confirm.isPending}
          >
            {confirm.isPending ? 'Confirmando...' : 'Ativar'}
          </button>
        </div>
      ) : (
        <div className="row" style={{ marginTop: 'var(--space-4)' }}>
          <button
            className="btn"
            onClick={() => startSetup.mutate()}
            disabled={startSetup.isPending}
          >
            {startSetup.isPending ? 'Gerando...' : 'Ativar verificacao'}
          </button>
        </div>
      )}
    </section>
  );
}

// -------------------------------------------------------------------- senha

function PasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [feedback, setFeedback] = useState<{ type: 'error' | 'success'; text: string } | null>(
    null,
  );

  const change = useMutation({
    mutationFn: () =>
      api.changePassword({ currentPassword, newPassword, revokeOtherSessions: true }),
    onSuccess: (data) => {
      setCurrentPassword('');
      setNewPassword('');
      setFeedback({
        type: 'success',
        text:
          data.revokedSessions > 0
            ? `Senha alterada. ${data.revokedSessions} outro(s) dispositivo(s) foram desconectados.`
            : 'Senha alterada.',
      });
    },
    onError: (caught) => setFeedback({ type: 'error', text: messageFor(caught) }),
  });

  return (
    <section className="card">
      <h2 className="card-title">Senha</h2>
      <p className="card-hint">Trocar a senha desconecta os outros dispositivos.</p>

      {feedback && (
        <div className={`alert ${feedback.type}`} role="alert">
          {feedback.text}
        </div>
      )}

      <div className="field">
        <label htmlFor="currentPassword">Senha atual</label>
        <input
          id="currentPassword"
          type="password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(event) => setCurrentPassword(event.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="newPassword">Nova senha</label>
        <input
          id="newPassword"
          type="password"
          autoComplete="new-password"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
        />
        <span className="field-hint">Ao menos 12 caracteres, com variedade.</span>
      </div>

      <button
        className="btn"
        onClick={() => change.mutate()}
        disabled={!currentPassword || newPassword.length < 12 || change.isPending}
      >
        {change.isPending ? 'Alterando...' : 'Alterar senha'}
      </button>
    </section>
  );
}

// -------------------------------------------------------------- dispositivos

function SessionsCard() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ['sessions'], queryFn: api.sessions });

  const revoke = useMutation({
    mutationFn: api.revokeSession,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sessions'] }),
  });

  return (
    <section className="card">
      <h2 className="card-title">Dispositivos conectados</h2>
      <p className="card-hint">
        Encerrar um dispositivo invalida o acesso dele na hora, sem esperar o token vencer.
      </p>

      {isLoading ? (
        <p className="muted">Carregando...</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>Dispositivo</th>
                <th>Endereco</th>
                <th>Ultimo uso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data?.sessions.map((session) => (
                <tr key={session.id}>
                  <td>
                    {shortenAgent(session.userAgent)}
                    {session.current && (
                      <>
                        {' '}
                        <span className="badge accent">este</span>
                      </>
                    )}
                  </td>
                  <td className="mono">{session.ipAddress ?? '—'}</td>
                  <td className="tabular">{formatDateTime(session.lastUsedAt)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {!session.current && (
                      <button
                        className="btn danger"
                        onClick={() => revoke.mutate(session.id)}
                        disabled={revoke.isPending}
                      >
                        Encerrar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function shortenAgent(userAgent: string | null): string {
  if (!userAgent) return 'Dispositivo desconhecido';
  if (/edg/i.test(userAgent)) return 'Edge';
  if (/chrome/i.test(userAgent)) return 'Chrome';
  if (/firefox/i.test(userAgent)) return 'Firefox';
  if (/safari/i.test(userAgent)) return 'Safari';
  if (/okhttp|expo|dart/i.test(userAgent)) return 'Aplicativo';
  return userAgent.slice(0, 40);
}

function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : 'Nao foi possivel concluir a operacao.';
}
